import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import messages from "../../../../../assets/locale/messages";
import { UIActionModal, UIButton, UISelectCard } from "../../../../ui";
import {
  CREW_SIZE_SALES_EMAIL,
  EVENT_MODELS,
  fillTemplate,
  formatAmount,
  formatCrewSizePrice,
  getCrewSizeFee,
  getSubscriptionPlanName,
} from "../../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import CustomPlanBanner from "../../../CustomPlanBanner";
import SummaryRow from "../../../Form/Steps/PublishAndPay/SummaryRow";
import {
  getEventRemainingDays,
  getUpgradeBillingModel,
  toUpgradeOptions,
} from "../../Roles/crewSizeUpgradeUtils";
import UpgradeApprovalNotice from "../UpgradeApprovalNotice";
import { getCompanyPortalEventCrewSizePlans } from "../../../../../network/apis/companyPortalEvents";
import { getPortalApiErrorMessage } from "../../../../../utils/companyPortal/eventApi";
import { showErrorMsg } from "../../../../../utils/Helpers";

import "../../../Form/Steps/PublishAndPay/PublishAndPay.scss";
import "../_CrewSizeUpgradeModals.scss";

/** Upgrade step: pick a crew size above the current one and see the difference to pay. */
const UpgradeCrewSizeModal = ({
  open,
  event,
  currentCrewSize,
  atCapacity,
  onClose,
  onContinue,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth?.login);
  const { events, shared } = messages[lang];
  const t = events.viewEvents.crewSizeUpgrade;
  const modalT = t.sizeModal;
  const crewSizeT = events.creatEvent.crewSize;

  const eventId = event?.id;
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    getCompanyPortalEventCrewSizePlans(
      eventId,
      { is_upgrade: true, page: -1 },
      // The error is toasted here once, not also by the interceptor.
      { handlerEnabled: false },
    )
      .then((response) => {
        if (cancelled) return;
        const next = toUpgradeOptions(response?.data?.data ?? []);
        setOptions(next);
        setSelectedId(next[0]?.id ?? null);
        setLoaded(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setFailed(true);
        showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, shared.generalErrMsg]);

  const selected = options.find((option) => option.id === selectedId);
  const days = getEventRemainingDays(event);
  const model = getUpgradeBillingModel(event);
  const isExtended = model === EVENT_MODELS.extended;
  const subscriptionPlan =
    event?.company?.subscription_plan ?? login?.company?.subscription_plan;
  const planName = getSubscriptionPlanName(subscriptionPlan, lang);
  const price = selected
    ? formatCrewSizePrice(crewSizeT, getCrewSizeFee(selected, model), model, planName)
    : null;
  const sizeLabel = (option) => option.name ?? crewSizeT.sizes[option.key];
  const currentSizeLabel = sizeLabel(currentCrewSize);
  // Above the biggest fixed plan the sales team takes over.
  const maxCapacity = Math.max(
    currentCrewSize.max,
    ...options.map((option) => option.max),
  );
  const isCustomPlan = Boolean(event?.crew_size_plan?.custom);
  // No bigger fixed size left: only the sales team can raise the ceiling.
  const noBiggerSize = loaded && options.length === 0;
  let description = atCapacity
    ? fillTemplate(modalT.atCapacityDescription, { size: currentSizeLabel })
    : modalT.description;
  if (isCustomPlan) {
    description = fillTemplate(modalT.customPlanDescription, {
      max: formatAmount(currentCrewSize.max),
    });
  } else if (noBiggerSize) {
    description = fillTemplate(modalT.biggestSizeDescription, {
      size: currentSizeLabel,
    });
  }

  // The buttons wait for the sizes, so Continue never shows for a moment on the biggest size
  // (PROLINE-4202). Nothing bigger to pay for, or no sizes at all: Cancel only.
  const renderActions = () => {
    if (!loaded && !failed) return null;
    if (failed || noBiggerSize) {
      return (
        <UIButton
          variant="outline"
          size="medium"
          className="crew-size-upgrade__cancel"
          onClick={onClose}
        >
          {shared.cancel}
        </UIButton>
      );
    }
    return (
      <UIActionModal.Actions
        cancelLabel={shared.cancel}
        confirmLabel={modalT.continue}
        confirmDisabled={selected?.upgradeCost == null}
        onCancel={onClose}
        onConfirm={() => onContinue(selected)}
      />
    );
  };

  return (
    <UIActionModal
      open={open}
      variant="info"
      className="crew-size-upgrade-modal"
      title={modalT.title}
      onClose={onClose}
      actions={renderActions()}
    >
      <UIActionModal.Divider />
      <div className="crew-size-upgrade__body">
        <Typography
          component="p"
          className="text-body-sm crew-size-upgrade__message"
        >
          {description}
        </Typography>
        <div className="crew-size-upgrade__section">
          <Typography
            component="p"
            className="text-label-md crew-size-upgrade__current"
          >
            {modalT.current}{" "}
            <strong>
              {fillTemplate(modalT.currentValue, {
                size: currentSizeLabel,
                max: formatAmount(currentCrewSize.max),
              })}
            </strong>
          </Typography>

          {options.length > 0 && (
            <div
              className="crew-size-upgrade__options"
              role="radiogroup"
              aria-label={modalT.title}
            >
              {options.map((option) => (
                <UISelectCard
                  key={option.id}
                  selectionRole="radio"
                  size="md"
                  title={sizeLabel(option)}
                  meta={[
                    fillTemplate(modalT.range, {
                      min: formatAmount(option.min),
                      max: formatAmount(option.max),
                    }),
                  ]}
                  selected={option.id === selectedId}
                  onClick={() => setSelectedId(option.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Its ceiling is the biggest loaded size, so it waits for the sizes. */}
        {loaded && (
          <CustomPlanBanner
            variant="subtle"
            title={modalT.customPlan}
            message={fillTemplate(
              isCustomPlan ? modalT.customPlanIncreaseMessage : modalT.customPlanMessage,
              { max: formatAmount(maxCapacity), email: CREW_SIZE_SALES_EMAIL },
            )}
          />
        )}

        {selected && (
          <>
            <div className="crew-size-upgrade__summary">
              <SummaryRow
                label={modalT.newCeiling}
                value={fillTemplate(modalT.newCeilingValue, {
                  max: formatAmount(selected.max),
                  current: formatAmount(currentCrewSize.max),
                })}
              />
              {/* An extended event pays a flat difference, with no day factor. */}
              {!isExtended && days.remaining != null && (
                <SummaryRow
                  label={modalT.remainingDays}
                  value={fillTemplate(modalT.remainingDaysValue, {
                    remaining: formatAmount(days.remaining),
                    total: formatAmount(days.total),
                  })}
                />
              )}
              {price && <SummaryRow label={modalT.rate} value={price} />}
              <SummaryRow
                total
                label={modalT.difference}
                value={
                  selected.upgradeCost == null
                    ? "—"
                    : `${formatAmount(selected.upgradeCost)} ${crewSizeT.currency}`
                }
              />
            </div>
            <UpgradeApprovalNotice t={t} />
          </>
        )}
      </div>
    </UIActionModal>
  );
};

UpgradeCrewSizeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The published event: its id, dates, `payment_plan` and `remaining_operating_days`. */
  event: PropTypes.object.isRequired,
  currentCrewSize: PropTypes.shape({
    key: PropTypes.string.isRequired,
    max: PropTypes.number.isRequired,
    name: PropTypes.string,
  }).isRequired,
  /** The event already uses its whole capacity. */
  atCapacity: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Called with the chosen crew size option (`id`, `name`, `max`, `upgradeCost`). */
  onContinue: PropTypes.func.isRequired,
};

UpgradeCrewSizeModal.defaultProps = {
  atCapacity: false,
};

export default UpgradeCrewSizeModal;
